import { useSupportAccounts } from '@my/directory-api/hooks';
import type { AppNameValue, SupportAccount } from '@my/directory-api';
import { ErrorBanner, Loading, PageHeader } from '@my/ui';
import { ChevronDown, ChevronRight, ChevronsUpDown, ChevronUp, Search } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { appPillClass } from './lib/appColours';

type SortKey = 'name' | 'domain';
type SortDir = 'asc' | 'desc';

/**
 * A–Z plus `#` for anything not starting with a letter (a name beginning with
 * a digit, an accent or a quote). Rendered in full and always in the same
 * order: a row of chips that appears and disappears as the data changes is
 * one you have to read every time instead of aiming at from memory.
 */
const LETTERS = [...'ABCDEFGHIJKLMNOPQRSTUVWXYZ', '#'];

/** Apps an account can hold. `Hub` is absent — every account has it. */
const APPS: AppNameValue[] = ['Tms', 'Depot', 'Subsistence', 'Safety', 'Check'];

type LetterFilter = 'all' | string;
type AppFilter = 'all' | 'none' | AppNameValue;

/** Which chip an account belongs under. */
function initialOf(name: string): string {
  const ch = name.trim().charAt(0).toUpperCase();
  return ch >= 'A' && ch <= 'Z' ? ch : '#';
}

/**
 * Every account on the platform.
 *
 * <p>
 * THE ONE STAFF-ONLY SCREEN. Choosing between accounts you are not a member of
 * is the thing a customer will never do, so this is the part of My Hub that
 * stays behind the super-user flag after the rest opens up — at which point a
 * customer lands straight on their own account's page instead.
 * </p>
 *
 * <p>
 * Chrome, filters and table are the parties-list pattern from TMS, down to the
 * sticky Actions column: an operator moving between the apps should not have to
 * relearn where the search box is.
 * </p>
 */
export function AccountsPage() {
  const navigate = useNavigate();
  const query = useSupportAccounts();

  const [search, setSearch] = useState('');
  const [letter, setLetter] = useState<LetterFilter>('all');
  const [filterApp, setFilterApp] = useState<AppFilter>('all');
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortKey(key);
    setSortDir('asc');
  };

  const hasActiveFilters = letter !== 'all' || filterApp !== 'all' || search.trim().length > 0;
  const clearFilters = () => {
    setLetter('all');
    setFilterApp('all');
    setSearch('');
  };

  // ACTIVE subscriptions only. The list answers "what is this customer using",
  // and a lapsed row would put an app pill on an account that cannot open it.
  // The subscriptions screen shows the dead ones, where they mean something.
  const liveApps = (account: SupportAccount) =>
    account.subscriptions.filter((s) => s.isActive).map((s) => s.appKey);

  const matchesApp = (account: SupportAccount) => {
    if (filterApp === 'all') return true;
    if (filterApp === 'none') return liveApps(account).length === 0;
    return liveApps(account).includes(filterApp);
  };

  // How many accounts sit under each initial, so empty letters can be dimmed
  // rather than hidden — with thousands of accounts most letters are live, and
  // a chip that vanishes moves every chip after it.
  //
  // Counted AFTER the app filter, so picking "Depot" greys out the letters no
  // Depot customer starts with. An alphabet that ignored the other filter would
  // keep offering letters that lead to an empty table.
  const countByLetter = useMemo(() => {
    const counts = new Map<string, number>();
    for (const account of query.data ?? []) {
      if (!matchesApp(account)) continue;
      const key = initialOf(account.name);
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return counts;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- matchesApp is derived from filterApp
  }, [query.data, filterApp]);

  const visible = useMemo(() => {
    let list = query.data ?? [];

    // The two chip rows narrow together rather than replacing each other:
    // "everyone on Depot whose name starts with M" is worth asking directly.
    if (letter !== 'all') list = list.filter((a) => initialOf(a.name) === letter);
    list = list.filter(matchesApp);

    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (a) => a.name.toLowerCase().includes(q) || a.domain.toLowerCase().includes(q),
      );
    }

    return applySort(list, sortKey, sortDir);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- matchesApp is derived from filterApp
  }, [query.data, letter, filterApp, search, sortKey, sortDir]);

  return (
    <div className="flex h-full flex-col px-2">
      <PageHeader title="Accounts" description="Every customer account on the platform." />

      <main className="flex min-h-0 flex-1 flex-col gap-3 overflow-auto pb-4">
        {query.isPending && <Loading label="Accounts" />}

        {query.isError && (
          <ErrorBanner title="Failed to load accounts" message={query.error.message} />
        )}

        {query.isSuccess && (
          <>
            {/* ── Filters + search, one row ─────────────────────────────────
                App chips, a divider, then the alphabet. Both narrow the same
                list, so splitting them across two rows read as two unrelated
                controls; the divider says "different question, same filter". */}
            <div className="flex flex-wrap items-center gap-1.5">
              {(['all', ...APPS, 'none'] as AppFilter[]).map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFilterApp(f)}
                  className={chipClass(filterApp === f)}
                >
                  {f === 'all' ? 'All apps' : f === 'none' ? 'No apps' : f}
                </button>
              ))}

              <div className="mx-1 h-4 w-px bg-slate-300" aria-hidden="true" />

              <button
                type="button"
                onClick={() => setLetter('all')}
                className={chipClass(letter === 'all')}
              >
                All
              </button>

              {LETTERS.map((l) => {
                const count = countByLetter.get(l) ?? 0;
                return (
                  <button
                    key={l}
                    type="button"
                    // Disabled rather than hidden: the alphabet stays in the
                    // same place whatever the data does, so the chip you want
                    // is always where it was last time.
                    disabled={count === 0}
                    title={count === 0 ? `No accounts starting with ${l}` : `${count} account(s)`}
                    onClick={() => setLetter(l)}
                    className={letterChipClass(letter === l, count === 0)}
                  >
                    {l}
                  </button>
                );
              })}

              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="ml-1 text-[10px] text-slate-400 underline hover:text-slate-600"
                >
                  Clear
                </button>
              )}

              <div className="flex-1" />

              {/* Search */}
              <div className="relative">
                <Search
                  className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400"
                  aria-hidden="true"
                />
                <input
                  type="text"
                  placeholder="Search accounts…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-52 rounded-full border border-slate-300 bg-white py-1.5 pl-8 pr-3 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>

              {/* No "+ Add" yet, deliberately: the only account-creation path on
                  the API takes a password, which would mean staff choosing a
                  customer's credential. A button that cannot honestly do the
                  thing is worse than no button. */}
            </div>

            {/* ── Table ─────────────────────────────────────────────────── */}
            <div className="overflow-x-auto rounded-md">
              <div className="min-w-[768px]">
                <table className="w-full table-auto text-xs">
                  <thead className="bg-slate-700 text-[11px] uppercase tracking-wide text-white">
                    <tr>
                      <SortableTh
                        label="Account"
                        sortKey="name"
                        active={sortKey}
                        dir={sortDir}
                        onSort={handleSort}
                      />
                      <SortableTh
                        label="Domain"
                        sortKey="domain"
                        active={sortKey}
                        dir={sortDir}
                        onSort={handleSort}
                      />
                      <Th>Apps</Th>
                      <Th align="center">Organisations</Th>
                      {/* Sticky-right Actions column — as in the parties list:
                          the table is wider than a phone viewport, so the
                          rightmost column would otherwise sit past max
                          horizontal scroll. The cell carries a solid
                          background so scrolling content cannot bleed
                          through. */}
                      <th className="sticky right-0 z-10 whitespace-nowrap bg-slate-700 px-3 py-2 text-right font-semibold">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 bg-white">
                    {visible.length === 0 ? (
                      <tr>
                        <td
                          colSpan={5}
                          className="px-3 py-6 text-center text-xs italic text-slate-400"
                        >
                          {hasActiveFilters
                            ? 'No accounts match this filter.'
                            : 'No accounts on the platform.'}
                        </td>
                      </tr>
                    ) : (
                      visible.map((account) => (
                        <AccountRow
                          key={account.id}
                          account={account}
                          onOpen={() => navigate(`/accounts/${account.id}`)}
                        />
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}

// ── Components ──────────────────────────────────────────────────────────────

function AccountRow({ account, onOpen }: { account: SupportAccount; onOpen: () => void }) {
  const live = account.subscriptions.filter((s) => s.isActive).map((s) => s.appKey);

  return (
    // `group` lets the sticky Actions cell pick up the row hover state via
    // `group-hover:bg-slate-50` — without it that cell stays solid white
    // through hover and looks disconnected from the rest of the row.
    <tr className="group cursor-pointer hover:bg-slate-50" onDoubleClick={onOpen}>
      <td className="whitespace-nowrap px-3 py-1 align-top text-xs font-semibold text-slate-900">
        {account.name}
      </td>
      <td className="whitespace-nowrap px-3 py-1 align-top text-xs text-slate-500">
        {account.domain}
      </td>
      <td className="px-3 py-1 align-top text-xs">
        {live.length === 0 ? (
          // Worth showing rather than hiding: an account with nothing live is
          // either mid-onboarding or lapsed, and both are reasons somebody
          // opened this screen.
          <span className="italic text-slate-300">None</span>
        ) : (
          <span className="inline-flex flex-wrap gap-1">
            {live.map((app) => (
              <span key={app} className={appPillClass(app)}>
                {app}
              </span>
            ))}
          </span>
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-1 text-center align-top text-xs text-slate-500">
        {account.organisations.length}
      </td>
      {/* Sticky-right pin — see the matching `<th>` comment above. */}
      <td className="sticky right-0 z-10 whitespace-nowrap bg-white px-3 py-1 text-right align-top group-hover:bg-slate-50">
        <button
          type="button"
          aria-label={`Open ${account.name}`}
          onClick={(e) => {
            e.stopPropagation();
            onOpen();
          }}
          className="inline-flex h-5 w-5 items-center justify-center rounded-sm text-blue-600 hover:bg-blue-50"
        >
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </td>
    </tr>
  );
}

// Local copies, as in the parties list: these are table-shaped helpers rather
// than design-system components, and @my/ui does not export them.
function Th({ children, align }: { children: React.ReactNode; align?: 'right' | 'center' }) {
  const alignClass =
    align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  return <th className={`whitespace-nowrap px-3 py-2 font-semibold ${alignClass}`}>{children}</th>;
}

function SortableTh({
  label,
  sortKey,
  active,
  dir,
  onSort,
  align,
}: {
  label: string;
  sortKey: SortKey;
  active: SortKey | null;
  dir: SortDir;
  onSort: (k: SortKey) => void;
  align?: 'right' | 'center';
}) {
  const isActive = active === sortKey;
  const Icon = isActive ? (dir === 'asc' ? ChevronUp : ChevronDown) : ChevronsUpDown;
  const alignClass =
    align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  return (
    <th
      className={`group cursor-pointer select-none whitespace-nowrap px-3 py-2 font-semibold ${alignClass}`}
      onClick={() => onSort(sortKey)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <Icon
          className={`h-3 w-3 flex-shrink-0 transition-opacity ${
            isActive ? 'opacity-100' : 'opacity-40 group-hover:opacity-70'
          }`}
          aria-hidden="true"
        />
      </span>
    </th>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function applySort(list: SupportAccount[], key: SortKey | null, dir: SortDir): SupportAccount[] {
  if (!key) {
    // Default: name ascending. Matches what operators expect on first paint.
    return [...list].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
    );
  }
  const mul = dir === 'asc' ? 1 : -1;
  return [...list].sort((a, b) => {
    switch (key) {
      case 'name':
        return mul * a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
      case 'domain':
        return mul * a.domain.localeCompare(b.domain, undefined, { sensitivity: 'base' });
    }
  });
}

function chipClass(active: boolean, disabled = false): string {
  const base = 'rounded-full border px-2.5 py-0.5 text-[10px] font-medium transition-colors';
  return `${base} ${chipColours(active, disabled)}`;
}

/**
 * A single-character chip.
 *
 * <p>
 * A fixed width CANNOT be bolted onto {@link chipClass}: its `px-2.5` eats 20
 * of the 24 pixels, leaving a 2px content box for `text-center` to centre
 * within — which is why the letters sat off to one side. A square flex box with
 * no horizontal padding centres properly and keeps every letter the same size,
 * so the row does not jitter as `W` and `I` alternate.
 * </p>
 */
function letterChipClass(active: boolean, disabled = false): string {
  const base =
    'inline-flex h-5 w-5 items-center justify-center rounded-full border text-[10px] font-medium leading-none transition-colors';
  return `${base} ${chipColours(active, disabled)}`;
}

function chipColours(active: boolean, disabled: boolean): string {
  if (disabled) return 'cursor-default border-slate-200 bg-white text-slate-300';
  return active
    ? 'border-slate-700 bg-slate-700 text-white'
    : 'border-slate-300 bg-white text-slate-500 hover:border-slate-500 hover:text-slate-700';
}
